import React, { useEffect, useRef } from 'react'
import { Link } from 'gatsby'
import { StaticImage } from 'gatsby-plugin-image'
import { css } from 'linaria'
import { useLocation } from '@reach/router'
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock'
import SmoothScroll from '~/components/Common/SmoothScroll'
import IconDownload from '~/images/icon/download.svg'
import IconLogin from '~/images/icon/login.svg'
import { STYLE } from '~/utils/constant'
import { rem } from '~/utils/helper'

interface MenuProps {
  isMenuShow: boolean
  hideMenu: () => void
}

type NavigationItem = {
  label: string
  path: string
  hash?: string
}

const features: NavigationItem[] = [
  {
    label: '3つのメリット',
    path: '/',
    hash: 'advantage',
  },
  {
    label: '選ばれる理由',
    path: '/',
    hash: 'reason',
  },
  {
    label: '機能',
    path: '/',
    hash: 'function',
  },
  {
    label: 'サポート体制',
    path: '/',
    hash: 'support',
  },
  {
    label: '料金プラン',
    path: '/',
    hash: 'price',
  },
  {
    label: '導入事例',
    path: '/',
    hash: 'case',
  },
  {
    label: 'トピックス',
    path: '/',
    hash: 'topics',
  },
  {
    label: 'FAQ',
    path: '/',
    hash: 'faq',
  },
]

const options: NavigationItem[] = [
  {
    label: 'バックグラウンド\nチェック',
    path: '/backgroundcheck',
    hash: '',
  },
]

const infomations: NavigationItem[] = [
  {
    label: '資料一覧',
    path: '/resources',
    hash: '',
  },
  {
    label: 'イベント',
    path: '/event',
    hash: '',
  },
  {
    label: 'お役立ち情報',
    path: '/news',
    hash: '',
  },
]

const menuStyle = css`
  & > .inner {
    position: fixed;
    z-index: var(--zindex-menu);
    overflow-y: auto;
    transition: opacity 0.24s var(--transition-md) 0s;
    opacity: 0;
    pointer-events: none;
    inset: 0;

    &.-is-show {
      opacity: 1;
      pointer-events: auto;
    }
  }

  & > .inner > .body {
    position: relative;
    z-index: 2;
    padding-top: calc(var(--height-header) + 32px);
    transform: translateY(-16px);
    transition: transform 0.24s var(--transition-md) 0s;
    background: var(--color-background-gray);

    @media screen and (${STYLE.MEDIA.TABLET}) {
      padding-top: calc(var(--height-header) + 64px);
      padding-bottom: 64px;
    }

    @media screen and (${STYLE.MEDIA.PC}) {
      padding-top: calc(var(--height-header-pc) + 64px);
    }
  }

  & > .inner.-is-show > .body {
    transform: translateY(0);
  }

  & > .inner > .body > .content {
    display: flex;
    flex-wrap: wrap;
    width: 250px;
    margin-right: auto;
    margin-left: auto;
    row-gap: 48px;
    column-gap: 24px;

    @media screen and (${STYLE.MEDIA.TABLET}) {
      display: grid;
      grid-column-gap: 24px;
      grid-row-gap: 24px;
      grid-template-columns: calc(50% - 12px) repeat(2, 1fr);
      grid-template-rows: repeat(2, auto);
      width: 640px;
    }
  }

  & > .inner > .body > .content > .item {
    width: 100%;

    &.-features {
      @media screen and (${STYLE.MEDIA.TABLET}) {
        grid-area: 1 / 1 / 3 / 2;
      }
    }

    &.-options {
      @media screen and (${STYLE.MEDIA.TABLET}) {
        grid-area: 1 / 2 / 3 / 3;
      }
    }

    &.-infomations {
      @media screen and (${STYLE.MEDIA.TABLET}) {
        grid-area: 1 / 3 / 2 / 4;
      }
    }

    &.-login {
      @media screen and (${STYLE.MEDIA.TABLET}) {
        grid-area: 2 / 3 / 3 / 4;
      }
    }
  }

  & > .inner > .body > .content > .item > .title {
    font-size: ${rem(13)};
  }

  & > .inner > .body > .content > .item > .body > .items {
    display: flex;
    flex-direction: column;
    margin-top: 12px;
    column-gap: 24px;
    font-size: ${rem(16)};
    row-gap: 12px;
  }

  & > .inner > .body > .content > .item.-features > .body > .items {
    flex-flow: wrap row;
  }

  & > .inner > .body > .content > .item > .body > .items > .item {
    position: relative;
    padding-left: 1em;

    &::before {
      content: '';
      position: absolute;
      top: 0.8em;
      left: 0;
      width: 8px;
      height: 2px;
      background: var(--color-background-black);
    }
  }

  & > .inner > .body > .content > .item.-features > .body > .items > .item {
    width: calc(50% - 12px);
  }

  & > .inner > .body > .content > .item.-login > .body > .items > .item {
    padding-left: 0;

    &::before {
      display: none;
    }
  }

  & > .inner > .body > .content > .item > .body > .items > .item > .button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    column-gap: 4px;
    color: var(--color-text);
    font-weight: 700;
    line-height: 1.3;
    text-decoration: none;
    white-space: pre-wrap;
  }

  & > .inner > .closearea {
    position: absolute;
    opacity: 0.7;
    background: var(--color-background-black);
    inset: 0;
  }
`

const appealStyle = css`
  max-width: 720px;
  margin-top: 32px;
  margin-right: auto;
  margin-left: auto;
  padding: 32px 24px;
  overflow: hidden;
  background: var(--color-gradient-key);

  @media screen and (${STYLE.MEDIA.TABLET}) {
    margin-top: 64px;
    padding: 36px;
    border-radius: 12px;
  }

  & > .title {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  & > .title > .text {
    position: relative;
    color: var(--color-text-bright);
    font-size: ${rem(16)};
    font-weight: 700;
    text-align: center;

    &::before,
    &::after {
      content: '';
      position: absolute;
      bottom: 2px;
      width: 2px;
      height: 20px;
      border-radius: 10px;
      background-color: var(--color-background-bright);
    }

    &::before {
      left: -12px;
      transform: rotate(-12deg);
    }

    &::after {
      right: -12px;
      transform: rotate(12deg);
    }
  }

  & > .body {
    display: flex;
    flex-direction: column;
    max-width: 640px;
    margin-top: 28px;
    margin-right: auto;
    margin-left: auto;
    row-gap: 24px;

    @media screen and (${STYLE.MEDIA.TABLET}) {
      flex-direction: row;
      column-gap: 32px;
    }
  }

  & > .body > .button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 64px;
    transition: var(--hover-button-transition);
    border: 1px solid var(--color-border-bright);
    border-radius: 100px;
    background: none;
    background: var(--color-background-bright);
    color: var(--color-text-bright);
    color: var(--color-text-link);
    font-size: ${rem(16)};
    font-weight: 700;
    text-decoration: none;

    @media screen and (${STYLE.MEDIA.TABLET}) {
      max-width: 400px;
    }

    &:hover {
      opacity: var(--hover-button-opacity);
    }

    &.-request {
      column-gap: 8px;
      border-color: var(--color-background-yellow700);
      background: var(--color-background-yellow700);
    }
  }

  & > .body > .button.-request > .image {
    width: 80px;
    height: auto;
    margin-top: -16px;
    margin-right: 8px;
    margin-left: -32px;
  }
`

const MenuComponent = ({ isMenuShow, hideMenu }: MenuProps) => {
  const { pathname } = useLocation()
  const menuRef = useRef(null)
  const links = (items: NavigationItem[]) => {
    return items.map((item: NavigationItem) => {
      const href = item.hash ? `${item.path}#${item.hash}` : item.path
      const link = () => {
        if (pathname === item.path && item.hash) {
          // Note: ページ内スクロール
          return (
            <SmoothScroll className="button" to={item.hash} href={href} onClick={() => hideMenu()}>
              {item.label}
            </SmoothScroll>
          )
        } else {
          return (
            <Link to={href} className="button" onClick={() => hideMenu()}>
              {item.label}
            </Link>
          )
        }
      }
      return (
        <li className="item" key={item.label}>
          {link()}
        </li>
      )
    })
  }

  const requestItem = (
    <>
      <StaticImage
        className="image"
        src="../../images/download.png"
        objectFit="cover"
        objectPosition="center center"
        placeholder="none"
        alt=""
      />
      資料ダウンロード
      <IconDownload className="icon" />
    </>
  )

  useEffect(() => {
    if (menuRef && menuRef.current) {
      // Note: メニュー表示時にスクロール禁止
      isMenuShow ? disableBodyScroll(menuRef.current) : enableBodyScroll(menuRef.current)
    }
  })

  return (
    <div className={menuStyle}>
      <div className={`inner${isMenuShow ? ' -is-show' : ''}`} ref={menuRef}>
        <div className="body">
          <dl className="content">
            <div className="item -features">
              <dt className="title">ASHIATOの特徴</dt>
              <dd className="body">
                <ul className="items">{links(features)}</ul>
              </dd>
            </div>
            <div className="item -options">
              <dt className="title">オプション機能</dt>
              <dd className="body">
                <ul className="items">{links(options)}</ul>
              </dd>
            </div>
            <div className="item -infomations">
              <dt className="title">お役立ち情報</dt>
              <dd className="body">
                <ul className="items">{links(infomations)}</ul>
              </dd>
            </div>
            <div className="item -login">
              <dt className="title">ご利用中の方</dt>
              <dd className="body">
                <ul className="items">
                  <li className="item">
                    <Link to="/login" className="button" onClick={() => hideMenu()}>
                      <IconLogin className="icon" />
                      ログイン
                    </Link>
                  </li>
                </ul>
              </dd>
            </div>
          </dl>
          <dl className={appealStyle}>
            <dt className="title">
              <span className="text">お問い合わせはこちらから！</span>
            </dt>
            <dd className="body">
              {pathname === '/' ? (
                <SmoothScroll className="button -request" to="request" href="/#request" onClick={() => hideMenu()}>
                  {requestItem}
                </SmoothScroll>
              ) : (
                <Link className="button -request" to="/#request" onClick={() => hideMenu()}>
                  {requestItem}
                </Link>
              )}
              <Link className="button" to="/inquiry/" onClick={() => hideMenu()}>
                お問い合わせ
              </Link>
            </dd>
          </dl>
        </div>
        <div className="closearea" onClick={() => hideMenu()}></div>
      </div>
    </div>
  )
}

export default MenuComponent
